import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { LanguageSelector } from '../components/LanguageSelector';
import { MyCompanionLogo } from '../components/MyCompanionLogo';
import SmartImage from '../components/SmartImage';

// Curated portraits used in the 1s-rotation on each gender card. The first
// entry is the "primary face" of the card (Sofia for woman, Lucas for man).
const genders = [
  {
    id: 'woman',
    photos: [
      '/media/gallery/_gender/gender-00.webp',
      '/media/gallery/_gender/gender-01.webp',
      '/media/gallery/_gender/gender-02.webp',
      '/media/gallery/_gender/gender-03.webp',
      '/media/gallery/_gender/gender-04.webp',
    ],
    ring: 'ring-terracotta-main/40 group-hover:ring-terracotta-main/70',
  },
  {
    id: 'man',
    photos: [
      '/media/gallery/_gender/gender-05.webp',
      '/media/gallery/_gender/gender-06.webp',
      '/media/gallery/_gender/gender-07.webp',
      '/media/gallery/_gender/gender-08.webp',
    ],
    ring: 'ring-sage-main/40 group-hover:ring-sage-main/70',
  },
];

export default function SelectGenderPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t, language } = useLanguage();

  // Shared rotation tick — every 1 second, increment. Each card modulos by its
  // own photos.length so the cards stay independent even if their array sizes differ.
  const [rotationTick, setRotationTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setRotationTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const genderLabels = {
    woman: { fr: 'Une Femme', en: 'A Woman', es: 'Una Mujer', de: 'Eine Frau', pt: 'Uma Mulher' },
    man: { fr: 'Un Homme', en: 'A Man', es: 'Un Hombre', de: 'Ein Mann', pt: 'Um Homem' }
  };

  const genderDescriptions = {
    woman: { fr: 'Conversations avec une partenaire féminine', en: 'Conversations with a female partner', es: 'Conversaciones con una compañera femenina', de: 'Gespräche mit einer weiblichen Partnerin', pt: 'Conversas com uma parceira feminina' },
    man: { fr: 'Conversations avec un partenaire masculin', en: 'Conversations with a male partner', es: 'Conversaciones con un compañero masculino', de: 'Gespräche mit einem männlichen Partner', pt: 'Conversas com um parceiro masculino' }
  };

  const handleSelect = (gender) => {
    navigate(`/select-character/${gender}`);
  };

  return (
    <div className="min-h-screen bg-velvet-bg">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-velvet_text-secondary hover:text-velvet_text-accent transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft size={20} />
            <MyCompanionLogo size="sm" />
          </button>
          <div className="flex items-center gap-4">
            <LanguageSelector />
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                onClick={logout}
                className="text-velvet_text-secondary hover:text-velvet_text-primary text-sm"
                data-testid="logout-button"
              >
                {t('logout')}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          <h1 className="font-heading text-4xl lg:text-5xl text-center gradient-text mb-4 animate-slideUp">
            {({fr: 'Qui souhaitez-vous rencontrer ?', en: 'Who would you like to meet?', es: '¿A quién te gustaría conocer?', de: 'Wen möchtest du kennenlernen?', pt: 'Quem gostarias de conhecer?'})[language] || 'Who would you like to meet?'}
          </h1>
          <p className="text-velvet_text-secondary text-center mb-16 max-w-xl mx-auto">
            {({fr: 'Choisissez le type de partenaire pour vos conversations', en: 'Choose the type of partner for your conversations', es: 'Elige el tipo de pareja para tus conversaciones', de: 'Wähle die Art des Partners für deine Gespräche', pt: 'Escolhe o tipo de parceiro para as tuas conversas'})[language] || 'Choose the type of partner for your conversations'}
          </p>

          <div className="grid md:grid-cols-2 gap-8 max-w-2xl mx-auto">
            {genders.map((gender, index) => (
              <button
                key={gender.id}
                onClick={() => handleSelect(gender.id)}
                className={`group relative glass rounded-2xl p-8 card-hover animate-slideUp stagger-${index + 1} text-center`}
                data-testid={`gender-card-${gender.id}`}
              >
                {/* Rotating character portraits — stacked SmartImages, crossfade every 1s.
                    All images are mounted at once so they preload before their turn,
                    and the swap is a pure opacity transition (GPU-accelerated, no flash).
                    SmartImage prevents the per-load flicker on navigation back/forth. */}
                <div className="relative w-32 h-32 mx-auto mb-6">
                  {gender.photos.map((url, photoIdx) => {
                    const active = photoIdx === rotationTick % gender.photos.length;
                    return (
                      <SmartImage
                        key={url}
                        src={url}
                        alt={genderLabels[gender.id][language] || genderLabels[gender.id].en}
                        loading="eager"
                        className={`absolute inset-0 w-32 h-32 rounded-full ring-4 ${gender.ring} shadow-lg group-hover:scale-105 transition-all duration-500 ${active ? 'opacity-100' : 'opacity-0'}`}
                      />
                    );
                  })}
                </div>
                
                {/* Title */}
                <h3 className="font-heading text-2xl text-velvet_text-primary mb-2">
                  {genderLabels[gender.id][language] || genderLabels[gender.id].en}
                </h3>
                
                {/* Description */}
                <p className="text-velvet_text-secondary text-sm">
                  {genderDescriptions[gender.id][language] || genderDescriptions[gender.id].en}
                </p>

                {/* Hover effect */}
                <div className="absolute inset-0 border-2 border-transparent group-hover:border-purple-main/30 rounded-2xl transition-colors" />
              </button>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
